import { useEffect, useRef, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'

export default function RichTextEditor({ value, onChange }) {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-brand-navy underline' } }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            TextStyle,
            Color,
        ],
        content: value || '',
        onUpdate({ editor }) {
            onChange(editor.getHTML())
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm prose-neutral max-w-none min-h-[200px] p-4 focus:outline-none',
            },
        },
    })

    // Sync external value changes (e.g. on initial data load)
    useEffect(() => {
        if (!editor) return
        const current = editor.getHTML()
        if (value !== current) {
            editor.commands.setContent(value || '', false)
        }
    }, [value, editor])

    if (!editor) return null

    return (
        <div className="border border-admin-border rounded-md overflow-hidden bg-white focus-within:ring-2 focus-within:ring-brand-navy/20 focus-within:border-brand-navy/40 transition-all">
            <Toolbar editor={editor} />
            <EditorContent editor={editor} />
        </div>
    )
}

// Prefixes bare domains/paths with a scheme so the Link extension's URL
// validation (which strips hrefs it doesn't recognise as a real URL) accepts
// it, and so the saved link actually navigates somewhere on the frontend
// instead of resolving as a relative path off the current page.
function normalizeUrl(url) {
    const trimmed = url.trim()
    if (!trimmed) return trimmed
    if (/^([a-z][a-z0-9+.-]*:|\/|#)/i.test(trimmed)) return trimmed
    return `https://${trimmed}`
}

function Toolbar({ editor }) {
    const colorSelectionRef = useRef(null)
    const linkSelectionRef = useRef(null)
    const [linkPanelOpen, setLinkPanelOpen] = useState(false)
    const [linkUrl, setLinkUrl] = useState('')
    const [linkText, setLinkText] = useState('')
    const [linkHasSelection, setLinkHasSelection] = useState(false)

    const btn = (active, title, onClick, children) => (
        <button
            type="button"
            title={title}
            onClick={onClick}
            className={`p-1.5 rounded transition-colors ${
                active
                    ? 'bg-brand-navy text-white'
                    : 'text-admin-text hover:bg-slate-100'
            }`}
        >
            {children}
        </button>
    )

    // window.prompt() shares the same focus-stealing problem as the native color
    // dialog (see the color picker below), so the selection is captured up front
    // and explicitly restored before the link mark is applied. A collapsed
    // selection (just a cursor, e.g. clicking to place a link before/after
    // other text) has no text to turn into a link, so it also needs a "Link
    // text" field — that text gets inserted at the cursor with the mark applied.
    const openLinkPanel = () => {
        const { from, to } = editor.state.selection
        linkSelectionRef.current = { from, to }
        const hasSelection = from !== to
        setLinkHasSelection(hasSelection)
        setLinkUrl(editor.getAttributes('link').href || '')
        setLinkText(hasSelection ? editor.state.doc.textBetween(from, to, ' ') : '')
        setLinkPanelOpen(true)
    }

    const applyLink = () => {
        const sel = linkSelectionRef.current
        const url = normalizeUrl(linkUrl)
        const hasSelection = sel && sel.from !== sel.to

        if (!url) {
            const chain = editor.chain().focus()
            if (hasSelection) chain.setTextSelection(sel)
            chain.extendMarkRange('link').unsetLink().run()
            setLinkPanelOpen(false)
            return
        }

        if (hasSelection) {
            editor.chain().focus().setTextSelection(sel).extendMarkRange('link').setLink({ href: url }).run()
        } else {
            // No text was selected — insert the "Link text" as new linked text
            // at the cursor, so a link can be placed before/after other text.
            const text = linkText.trim() || url
            editor.chain().focus().setTextSelection(sel ? sel.from : editor.state.selection.from)
                .insertContent({ type: 'text', text, marks: [{ type: 'link', attrs: { href: url } }] })
                .run()
        }
        setLinkPanelOpen(false)
    }

    const removeLink = () => {
        const sel = linkSelectionRef.current
        const chain = editor.chain().focus()
        if (sel && sel.from !== sel.to) chain.setTextSelection(sel)
        chain.extendMarkRange('link').unsetLink().run()
        setLinkPanelOpen(false)
    }

    return (
        <div>
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-admin-border bg-slate-50">
            {/* Text style */}
            {btn(editor.isActive('bold'), 'Bold', () => editor.chain().focus().toggleBold().run(), <BoldIcon />)}
            {btn(editor.isActive('italic'), 'Italic', () => editor.chain().focus().toggleItalic().run(), <ItalicIcon />)}
            {btn(editor.isActive('underline'), 'Underline', () => editor.chain().focus().toggleUnderline().run(), <UnderlineIcon />)}

            <Divider />

            {/* Headings */}
            {btn(editor.isActive('heading', { level: 2 }), 'Heading 2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <span className="text-xs font-bold w-5 h-5 flex items-center justify-center">H2</span>)}
            {btn(editor.isActive('heading', { level: 3 }), 'Heading 3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), <span className="text-xs font-bold w-5 h-5 flex items-center justify-center">H3</span>)}

            <Divider />

            {/* Lists */}
            {btn(editor.isActive('bulletList'), 'Bullet list', () => editor.chain().focus().toggleBulletList().run(), <BulletListIcon />)}
            {btn(editor.isActive('orderedList'), 'Ordered list', () => editor.chain().focus().toggleOrderedList().run(), <OrderedListIcon />)}

            <Divider />

            {/* Alignment */}
            {btn(editor.isActive({ textAlign: 'left' }), 'Align left', () => editor.chain().focus().setTextAlign('left').run(), <AlignLeftIcon />)}
            {btn(editor.isActive({ textAlign: 'center' }), 'Align center', () => editor.chain().focus().setTextAlign('center').run(), <AlignCenterIcon />)}
            {btn(editor.isActive({ textAlign: 'right' }), 'Align right', () => editor.chain().focus().setTextAlign('right').run(), <AlignRightIcon />)}

            <Divider />

            {/* Link */}
            {btn(editor.isActive('link') || linkPanelOpen, 'Insert/edit link', openLinkPanel, <LinkIcon />)}

            {/* Horizontal rule */}
            {btn(false, 'Horizontal rule', () => editor.chain().focus().setHorizontalRule().run(), <HrIcon />)}

            <Divider />

            {/* Text color */}
            <div className="flex items-center gap-1">
                <input
                    type="color"
                    title="Text color"
                    value={editor.getAttributes('textStyle').color || '#000000'}
                    // Opening the native color dialog steals DOM focus/selection from the
                    // editor, so the selection the user made is captured up front (on
                    // pointer down, before the dialog opens) and explicitly restored
                    // before applying the color — relying on the editor to "remember"
                    // the selection through the focus loss is not reliable cross-browser.
                    onPointerDown={() => {
                        const { from, to } = editor.state.selection
                        colorSelectionRef.current = { from, to }
                    }}
                    onInput={(e) => {
                        const sel = colorSelectionRef.current
                        const chain = editor.chain().focus()
                        if (sel && sel.from !== sel.to) chain.setTextSelection(sel)
                        chain.setColor(e.target.value).run()
                    }}
                    className="w-7 h-7 rounded cursor-pointer border border-admin-border bg-white p-0.5"
                />
                {editor.getAttributes('textStyle').color && (
                    <button
                        type="button"
                        title="Clear color"
                        onClick={() => editor.chain().focus().unsetColor().run()}
                        className="p-1.5 rounded text-admin-text hover:bg-slate-100 transition-colors"
                    >
                        <ClearColorIcon />
                    </button>
                )}
            </div>

            <Divider />

            {/* Undo / Redo */}
            <button
                type="button"
                title="Undo"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                className="p-1.5 rounded text-admin-text hover:bg-slate-100 disabled:opacity-30 transition-colors"
            >
                <UndoIcon />
            </button>
            <button
                type="button"
                title="Redo"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                className="p-1.5 rounded text-admin-text hover:bg-slate-100 disabled:opacity-30 transition-colors"
            >
                <RedoIcon />
            </button>
        </div>
        {linkPanelOpen && (
            <div className="flex flex-wrap items-center gap-2 px-2 py-1.5 border-b border-admin-border bg-slate-50">
                {!linkHasSelection && (
                    <input
                        type="text"
                        autoFocus
                        value={linkText}
                        onChange={(e) => setLinkText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); applyLink() }
                            if (e.key === 'Escape') { e.preventDefault(); setLinkPanelOpen(false) }
                        }}
                        placeholder="Link text"
                        className="flex-1 min-w-[8rem] px-2 py-1 text-xs bg-white border border-admin-border rounded focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
                    />
                )}
                <input
                    type="text"
                    autoFocus={linkHasSelection}
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); applyLink() }
                        if (e.key === 'Escape') { e.preventDefault(); setLinkPanelOpen(false) }
                    }}
                    placeholder="https://example.com"
                    className="flex-1 min-w-[10rem] px-2 py-1 text-xs bg-white border border-admin-border rounded focus:outline-none focus:ring-2 focus:ring-brand-navy/20"
                />
                <button type="button" onClick={applyLink} className="px-3 py-1 text-xs font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all">
                    Apply
                </button>
                {editor.isActive('link') && (
                    <button type="button" onClick={removeLink} className="px-3 py-1 text-xs font-bold text-red-500 border border-red-200 rounded hover:bg-red-50 transition-all">
                        Remove
                    </button>
                )}
                <button type="button" onClick={() => setLinkPanelOpen(false)} className="px-3 py-1 text-xs font-bold text-admin-muted hover:text-admin-text transition-all">
                    Cancel
                </button>
            </div>
        )}
        </div>
    )
}

function Divider() {
    return <span className="w-px h-5 bg-admin-border mx-1" />
}

function BoldIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 4h8a4 4 0 010 8H6V4zM6 12h9a4 4 0 010 8H6v-8z" /></svg>
}
function ItalicIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 4h4M8 20h4M12 4l-4 16" /></svg>
}
function UnderlineIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v8a5 5 0 0010 0V4M5 20h14" /></svg>
}
function BulletListIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h16M4 18h16M4 6a1 1 0 110-2 1 1 0 010 2zM4 12a1 1 0 110-2 1 1 0 010 2zM4 18a1 1 0 110-2 1 1 0 010 2z" /></svg>
}
function OrderedListIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>
}
function AlignLeftIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 6h18M3 12h12M3 18h15" /></svg>
}
function AlignCenterIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 6h18M6 12h12M4.5 18h15" /></svg>
}
function AlignRightIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 6h18M9 12h12M6 18h15" /></svg>
}
function LinkIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
}
function ColorIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 20h16M7 16l5-13 5 13M7.5 12.5h9" /></svg>
}
function ClearColorIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 20h16M7 16l5-13 5 13M7.5 12.5h9M4 4l16 16" /></svg>
}
function HrIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" /></svg>
}
function UndoIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 10h10a5 5 0 015 5v1M3 10l4-4M3 10l4 4" /></svg>
}
function RedoIcon() {
    return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M21 10H11a5 5 0 00-5 5v1M21 10l-4-4M21 10l-4 4" /></svg>
}
